<template>
  <header class="app-header">
    <div class="navigation-bar">
      <router-link v-if="isLoggedIn" to="/" class="navigation-bar__logo" :class="{ 'navigation-bar__logo--inter': isInter }"/>
      <a
        v-else
        class="navigation-bar__logo"
        :class="{ 'navigation-bar__logo--inter': isInter }"
        :href="logoLink"
        target="_blank"
        rel="noreferrer"
        data-ua="click-nav-portal-login-logoacerto"
      />
      <LogoCredor
        v-if="dividaDestaqueCredor"
        class="navigation-bar__logo-credor-destaque"
        :credor="dividaDestaqueCredor.credor"
        :divida="dividaDestaqueCredor"
      />
      <!--
        Alvo do menu de abas do teste seu_foco_agora (ALVO_ABAS_HEADER_SEU_FOCO_AGORA). Só com sessão:
        a home é logada, e vazio ele ocuparia o meio da barra e tiraria a logo do centro no deslogado.
      -->
      <div v-if="isLoggedIn" id="app-header-abas" class="navigation-bar__abas" />
      <div v-if="isLoggedIn" class="navigation-bar__buttons">
        <AppNavigation />
      </div>
    </div>
  </header>
</template>

<script lang="ts">
import { storeToRefs } from 'pinia';
import { computed, defineComponent, ref, watch } from 'vue';

import AppNavigation from '@/components/AppNavigation.vue';
import LogoCredor from '@/components/LogoCredor.vue';

import { sessionService } from '@/services';
import theme, { Theme } from '@/services/ThemeService';

import useGlobalStore from '@/stores/global';
import useHeaderCredorDestaqueStore from '@/stores/useHeaderCredorDestaqueStore';

type LandingCredorKey = Exclude<Theme, 'portal' | 'acerto'>;

const isInter = theme.getCurrentTheme() === 'inter' || appSettings.VUE_APP_THEME === 'inter';

function getLandingUrl(credor: Theme) {
  if (credor === 'portal' || credor === 'acerto') return appSettings.VUE_APP_URL_SITE_INSTITUCIONAL;

  return `${appSettings.VUE_APP_URL_SITE_INSTITUCIONAL}limpar-meu-nome/${credor}`;
}

const landingPageUrls: Record<LandingCredorKey | 'default', string> = {
  inter: getLandingUrl('inter'),
  default: getLandingUrl('portal'),
};

function hasLandingPage(credor?: string): credor is keyof typeof landingPageUrls {
  return !!credor && credor in landingPageUrls;
}

const getLogoLink = () => {
  const logoUrlParts = window.getComputedStyle(document.documentElement).getPropertyValue('--logo-image').split('/');

  for (let i = 0; i < logoUrlParts.length; i += 1) {
    const part = logoUrlParts[i].toLowerCase();

    if (hasLandingPage(part)) return landingPageUrls[part];
  }

  return landingPageUrls.default;
};

export default defineComponent({
  components: { AppNavigation, LogoCredor },
  setup() {
    const isLoggedIn = computed(() => sessionService.isLoggedIn);
    const globalStore = useGlobalStore();
    const logoLink = ref();
    const { divida: dividaDestaqueCredor } = storeToRefs(useHeaderCredorDestaqueStore());

    watch(
      () => globalStore.loadedTheme,
      () => {
        logoLink.value = getLogoLink();
      },
      { immediate: true }
    );

    return {
      sessionService,
      isLoggedIn,
      logoLink,
      isInter,
      dividaDestaqueCredor,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/styles/vars";

.app-header {
  position: relative;
  user-select: none;
  z-index: 1000;

  @include responsive($desktop) {
    background: var(--acerto-menu-bg);
    box-shadow: inset 0 -3px 0 var(--acerto-menu-border-color);
  }
}

.navigation-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--acerto-menu-bg);
  height: 55px;
  padding: 10px 16px;
  border-bottom: 3px solid var(--acerto-menu-border-color);

  @include responsive($desktop) {
    @include shell-aligned-padding;
    height: 70px;
    background: none;
    border-bottom: 0;
  }
}

.navigation-bar__logo {
  justify-self: flex-start;
  /* Um pouco menor no mobile. Proporcional para os temas que definem a própria `--logo-width`. */
  width: calc(var(--logo-width) * 0.85);
  background-image: var(--logo-image);
  background-repeat: no-repeat;
  background-position: 0 50%;
  background-size: contain;
  height: 100%;
  margin-right: 5px;

  /*
   * Tema de marca própria entrega `--logo-mask` (SVG) e `--logo-color`, e a logo é pintada como
   * máscara — vetor, então nítida em qualquer DPR, e com a cor sob controle de quem exibe. Tema de
   * parceiro não define nenhuma das duas: `var()` sem valor invalida a declaração, as três caem no
   * valor inicial e sobra o `background-image` colorido de sempre.
   */
  -webkit-mask: var(--logo-mask) no-repeat 0 50% / contain;
  mask: var(--logo-mask) no-repeat 0 50% / contain;
  /*
   * `--logo-tem-mascara` é um interruptor de valor vazio, declarado só pelos temas de marca
   * própria. Sem ele o `var()` não tem fallback e invalida a declaração inteira, então o tema de
   * parceiro nunca ganha fundo sólido atrás do PNG — nem quando o bloco do teste A/B troca
   * `--logo-color` para preto ou branco no `<body>`.
   */
  background-color: var(--logo-tem-mascara) var(--logo-color);

  @include responsive($desktop) {
    width: var(--logo-width);
    margin-right: 30px;
  }
}

.navigation-bar__logo-credor-destaque {
  width: auto;
  height: 100%;
  flex-shrink: 0;
}

.navigation-bar__abas {
  display: flex;
  flex: 1;
  justify-content: center;
  height: 100%;
}

.navigation-bar__buttons {
  margin-left: auto;
}

</style>
